import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import DocumentationViewer from '../DocumentationViewer';
import DocumentationEditor from '../DocumentationEditor';
import IconUpload from '../IconUpload';
import Portal from '../Portal';
import FilterSidebar from '../FilterSidebar';
import ConfirmationModal from '../ConfirmationModal';

const MCPServerManagement = () => {
  const { isDarkMode } = useTheme();
  const { showNotification } = useNotification();
  const [servers, setServers] = useState([]);
  const [filteredServers, setFilteredServers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [selectedServers, setSelectedServers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, serverId: null, isBulk: false });
  
  // Filter and sort state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    endpoint: '',
    status: 'UNAVAILABLE',
    documentation: '',
    category: '',
    tags: '',
    iconAlt: ''
  });
  const [selectedServer, setSelectedServer] = useState(null);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [showDocumentationEditor, setShowDocumentationEditor] = useState(false);
  const [showIconUpload, setShowIconUpload] = useState(false);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isImportingCSV, setIsImportingCSV] = useState(false);

  useEffect(() => {
    fetchServers();
  }, []);

  // Get unique categories and statuses from servers
  const categories = useMemo(() => {
    const categoryMap = {};
    servers.forEach(server => {
      if (server.category) {
        categoryMap[server.category] = (categoryMap[server.category] || 0) + 1;
      }
    });
    
    // Add "All" option at the beginning
    const allCategories = [
      { value: 'ALL', label: 'All', count: servers.length }
    ];
    
    // Add specific categories
    Object.entries(categoryMap).forEach(([category, count]) => {
      allCategories.push({
        value: category,
        label: category,
        count
      });
    });
    
    return allCategories;
  }, [servers]);

  const statuses = useMemo(() => {
    const statusMap = {};
    servers.forEach(server => {
      statusMap[server.status] = (statusMap[server.status] || 0) + 1;
    });
    
    // Ensure both AVAILABLE and UNAVAILABLE are always present
    const allStatuses = [
      { value: 'AVAILABLE', label: 'Available', count: statusMap['AVAILABLE'] || 0 },
      { value: 'UNAVAILABLE', label: 'Unavailable', count: statusMap['UNAVAILABLE'] || 0 }
    ];
    
    return allStatuses;
  }, [servers]);

  // Comprehensive filtering and sorting
  const processedServers = useMemo(() => {
    let filtered = [...servers];

    // Search filter - only search by name and description
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(server =>
        server.name.toLowerCase().includes(term) ||
        (server.description && server.description.toLowerCase().includes(term))
      );
    }

    // Category filter
    if (selectedCategories.length > 0 && !selectedCategories.includes('ALL')) {
      filtered = filtered.filter(server => 
        server.category && selectedCategories.includes(server.category)
      );
    }

    // Status filter
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(server => 
        selectedStatuses.includes(server.status)
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'updated':
          return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
        default:
          return 0;
      }
    });

    return filtered;
  }, [servers, searchTerm, selectedCategories, selectedStatuses, sortBy]);

  useEffect(() => {
    setFilteredServers(processedServers);
  }, [processedServers]);

  useEffect(() => {
    setSelectAll(selectedServers.length > 0 && selectedServers.length === filteredServers.length);
  }, [selectedServers, filteredServers]);

  const fetchServers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/mcp-servers');
      setServers(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch MCP servers');
      console.error('Error fetching servers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingServer) {
        await apiClient.put(`/mcp-servers/${editingServer.id}`, formData);
      } else {
        await apiClient.post('/mcp-servers', formData);
      }
      
      setShowForm(false);
      setEditingServer(null);
      setFormData({ name: '', description: '', endpoint: '', status: 'UNAVAILABLE' });
      fetchServers();
    } catch (err) {
      setError('Failed to save MCP server');
      console.error('Error saving server:', err);
    }
  };

  const handleEdit = (server) => {
    setEditingServer(server);
    setFormData({
      name: server.name,
      description: server.description || '',
      endpoint: server.endpoint,
      status: server.status
    });
    setShowForm(true);
  };

  const handleDelete = (serverId) => {
    setDeleteConfirm({ isOpen: true, serverId, isBulk: false });
  };

  const confirmDelete = async () => {
    const { serverId, isBulk } = deleteConfirm;
    
    try {
      if (isBulk) {
        const deletePromises = selectedServers.map(id => 
          apiClient.delete(`/mcp-servers/${id}`)
        );
        await Promise.all(deletePromises);
        
        setServers(servers.filter(server => !selectedServers.includes(server.id)));
        setSelectedServers([]);
        setSelectAll(false);
        showNotification(`Successfully deleted ${selectedServers.length} server(s)`, 'success');
      } else {
        await apiClient.delete(`/mcp-servers/${serverId}`);
        setServers(servers.filter(server => server.id !== serverId));
        showNotification('MCP server deleted successfully', 'success');
      }
    } catch (err) {
      const errorMessage = isBulk 
        ? 'Failed to delete some servers' 
        : 'Failed to delete MCP server';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error deleting server:', err);
    } finally {
      setDeleteConfirm({ isOpen: false, serverId: null, isBulk: false });
    }
  };

  const handleStatusChange = async (serverId, newStatus) => {
    try {
      await apiClient.patch(`/mcp-servers/${serverId}/status`, { status: newStatus });
      fetchServers();
    } catch (err) {
      setError('Failed to update server status');
      console.error('Error updating status:', err);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedServers(filteredServers.map(server => server.id));
    } else {
      setSelectedServers([]);
    }
  };

  const handleSelectServer = (serverId, checked) => {
    if (checked) {
      setSelectedServers([...selectedServers, serverId]);
    } else {
      setSelectedServers(selectedServers.filter(id => id !== serverId));
    }
  };

  const handleBulkDelete = () => {
    if (selectedServers.length === 0) {
      setError('Please select servers to delete');
      return;
    }
    setDeleteConfirm({ isOpen: true, serverId: null, isBulk: true });
  };

  const handleViewDocumentation = (server) => {
    setSelectedServer(server);
    setShowDocumentation(true);
  };

  const handleEditDocumentation = (server) => {
    setSelectedServer(server);
    setShowDocumentationEditor(true);
  };

  const handleSaveDocumentation = async (docData) => {
    try {
      await apiClient.patch(`/mcp-servers/${selectedServer.id}/documentation`, {
        documentation: docData.documentation,
        category: docData.category,
        tags: docData.tags,
        iconAlt: docData.iconAlt,
        documentedBy: 'Admin' // In a real app, get from auth context
      });
      
      setShowDocumentationEditor(false);
      setSelectedServer(null);
      fetchServers();
    } catch (err) {
      setError('Failed to save documentation');
      console.error('Error saving documentation:', err);
    }
  };

  const handleUploadIcon = async (file) => {
    setIsUploadingIcon(true);
    try {
      const formData = new FormData();
      formData.append('icon', file);
      formData.append('iconAlt', selectedServer?.iconAlt || '');

      await apiClient.patch(`/mcp-servers/${selectedServer.id}/icon`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setShowIconUpload(false);
      setSelectedServer(null);
      fetchServers();
    } catch (err) {
      setError('Failed to upload icon');
      console.error('Error uploading icon:', err);
    } finally {
      setIsUploadingIcon(false);
    }
  };

  const handleRemoveIcon = async () => {
    try {
      await apiClient.patch(`/mcp-servers/${selectedServer.id}/icon`, {
        iconUrl: null,
        iconAlt: ''
      });
      
      setShowIconUpload(false);
      setSelectedServer(null);
      fetchServers();
    } catch (err) {
      setError('Failed to remove icon');
      console.error('Error removing icon:', err);
    }
  };

  const handleIconAltChange = (altText) => {
    if (selectedServer) {
      setSelectedServer({ ...selectedServer, iconAlt: altText });
    }
  };

  // Filter and sort handlers
  const handleCategoryChange = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleStatusFilterChange = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedStatuses([]);
    setSearchTerm('');
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  const handleExportCSV = async () => {
    try {
      setIsExportingCSV(true);
      const response = await apiClient.get('/mcp-servers/export/csv', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'mcp-servers.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export CSV');
      console.error('Error exporting CSV:', err);
    } finally {
      setIsExportingCSV(false);
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    try {
      setIsImportingCSV(true);
      const formData = new FormData();
      formData.append('csvFile', file);

      const response = await apiClient.post('/mcp-servers/import/csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const { created, updated, errors } = response.data.data;
        let message = `Import completed: ${created} created, ${updated} updated`;
        const notificationType = errors.length > 0 ? 'warning' : 'success';
        if (errors.length > 0) {
          message += `, ${errors.length} error${errors.length > 1 ? 's' : ''}`;
        }
        showNotification(message, notificationType, 5000);
        fetchServers(); // Refresh the list
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to import CSV';
      setError(errorMessage);
      showNotification(errorMessage, 'error', 5000);
      console.error('Error importing CSV:', err);
    } finally {
      setIsImportingCSV(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'status', label: 'Status' },
    { value: 'category', label: 'Category' },
    { value: 'created', label: 'Date Created' },
    { value: 'updated', label: 'Last Updated' }
  ];


  if (loading) {
    return <div className="flex-center">Loading MCP servers...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>MCP Server Management</h2>
        <div className="flex gap-10" style={{ alignItems: 'center' }}>
          <button
            className="btn btn-success"
            onClick={handleExportCSV}
            disabled={isExportingCSV}
            style={{ 
              opacity: isExportingCSV ? 0.6 : 1,
              cursor: isExportingCSV ? 'not-allowed' : 'pointer'
            }}
          >
            {isExportingCSV ? 'Exporting...' : 'Export CSV'}
          </button>
          <label className="btn btn-info" style={{ cursor: 'pointer', margin: 0 }}>
            {isImportingCSV ? 'Importing...' : 'Import CSV'}
            <input
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              disabled={isImportingCSV}
              style={{ display: 'none' }}
            />
          </label>
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowForm(!showForm);
              setEditingServer(null);
              setFormData({ name: '', description: '', endpoint: '', status: 'UNAVAILABLE' });
            }}
          >
            {showForm ? 'Cancel' : 'Add Server'}
          </button>
        </div>
      </div>


      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3>{editingServer ? 'Edit MCP Server' : 'Add New MCP Server'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="name">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                className="form-input"
                value={formData.description}
                onChange={handleChange}
                rows="3"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="endpoint">
                Endpoint *
              </label>
              <input
                type="url"
                id="endpoint"
                name="endpoint"
                className="form-input"
                value={formData.endpoint}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="status">
                Status
              </label>
              <select
                id="status"
                name="status"
                className="form-input"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="UNAVAILABLE">Unavailable</option>
                <option value="AVAILABLE">Available</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="category">
                Category
              </label>
              <input
                type="text"
                id="category"
                name="category"
                className="form-input"
                value={formData.category}
                onChange={handleChange}
                placeholder="e.g., Bioinformatics, Data Analysis"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="tags">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                id="tags"
                name="tags"
                className="form-input"
                value={formData.tags}
                onChange={handleChange}
                placeholder="e.g., genomics, python, analysis"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="iconAlt">
                Icon Alt Text
              </label>
              <input
                type="text"
                id="iconAlt"
                name="iconAlt"
                className="form-input"
                value={formData.iconAlt}
                onChange={handleChange}
                placeholder="Descriptive text for the icon"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="documentation">
                Documentation (Markdown)
              </label>
              <textarea
                id="documentation"
                name="documentation"
                className="form-input"
                value={formData.documentation}
                onChange={handleChange}
                rows="6"
                placeholder="Write documentation in Markdown format..."
              />
            </div>

            <div className="flex gap-10">
              <button type="submit" className="btn btn-primary">
                {editingServer ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowForm(false);
                  setEditingServer(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Main Content with Sidebar */}
      <div className="mcp-servers-layout">
        {/* Left Side: Filter Sidebar */}
        <div className="filter-sidebar-wrapper">
          <FilterSidebar
            categories={categories}
            statuses={statuses}
            selectedCategories={selectedCategories}
            selectedStatuses={selectedStatuses}
            onCategoryChange={handleCategoryChange}
            onStatusChange={handleStatusFilterChange}
            onClearFilters={handleClearFilters}
            title="Filters"
          />
        </div>

        {/* Right Side: Search, Sort, and Main Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Search and Sort Controls */}
          <div className="search-sort-container" style={{
            backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
            borderRadius: '8px',
            padding: 'clamp(1rem, 3vw, 1.5rem)',
            marginBottom: '1.5rem'
          }}>
            {/* Search Element */}
            <div className="search-wrapper" style={{ position: 'relative' }}>
              <div style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#007bff',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 2.5rem',
                  border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                  color: isDarkMode ? 'white' : '#333',
                  fontSize: '0.9rem',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#007bff';
                  e.target.style.boxShadow = '0 0 0 2px rgba(0, 123, 255, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#555' : '#ddd';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Sort Element - Anchored to the right */}
            <div className="sort-wrapper" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              marginLeft: 'auto'
            }}>
              <span style={{
                color: isDarkMode ? '#aaa' : '#666',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                Showing {filteredServers.length} of {servers.length}
              </span>
              
              <div style={{ position: 'relative' }}>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                    border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                    borderRadius: '6px',
                    color: isDarkMode ? 'white' : '#333',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#f8f9fa';
                    e.target.style.borderColor = '#007bff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#2a2a2a' : 'white';
                    e.target.style.borderColor = isDarkMode ? '#555' : '#ddd';
                  }}
                >
                  Sort by {sortOptions.find(opt => opt.value === sortBy)?.label || 'popularity'}
                  <span>▼</span>
                </button>
                
                {/* Dropdown Menu */}
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                  border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '150px',
                  marginTop: '0.25rem',
                  display: 'none' // This would be controlled by state in a real implementation
                }}>
                  {sortOptions.map((option) => (
                    <div
                      key={option.value}
                      style={{
                        padding: '0.75rem 1rem',
                        cursor: 'pointer',
                        color: isDarkMode ? 'white' : '#333',
                        fontSize: '0.9rem',
                        borderBottom: `1px solid ${isDarkMode ? '#333' : '#f0f0f0'}`,
                        transition: 'background-color 0.2s ease'
                      }}
                      onClick={() => handleSortChange(option.value)}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          {filteredServers.length === 0 ? (
            <div className="card">
              <p>{servers.length === 0 ? 'No MCP servers found.' : 'No matching servers found.'}</p>
            </div>
          ) : (
            <div>
              {/* Selection Controls */}
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div className="flex-between" style={{ alignItems: 'center' }}>
                  <div className="flex" style={{ alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    <label style={{ margin: 0, fontWeight: '500' }}>
                      Select All ({filteredServers.length} servers)
                    </label>
                  </div>
                  <button
                    className="btn btn-danger"
                    onClick={handleBulkDelete}
                    disabled={selectedServers.length === 0}
                    style={{ 
                      opacity: selectedServers.length === 0 ? 0.5 : 1,
                      cursor: selectedServers.length === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Delete Selected ({selectedServers.length})
                  </button>
                </div>
              </div>

              <div className="card">
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <table className="table" style={{ minWidth: '600px' }}>
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredServers.map((server) => (
                        <tr key={server.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedServers.includes(server.id)}
                              onChange={(e) => handleSelectServer(server.id, e.target.checked)}
                              style={{ marginRight: '8px' }}
                            />
                          </td>
                          <td>{server.name}</td>
                          <td>{server.description || '-'}</td>
                          <td>
                            <select
                              value={server.status}
                              onChange={(e) => handleStatusChange(server.id, e.target.value)}
                              style={{
                                padding: '2px 6px',
                                fontSize: '12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                            >
                              <option value="UNAVAILABLE">Unavailable</option>
                              <option value="AVAILABLE">Available</option>
                            </select>
                          </td>
                          <td>
                            <div className="flex gap-10" style={{ alignItems: 'center', flexWrap: 'nowrap' }}>
                              <button
                                className="btn btn-secondary"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                onClick={() => handleEdit(server)}
                              >
                                Edit
                              </button>
                              {server.documentation && (
                                <button
                                  className="btn btn-info"
                                  style={{ padding: '4px 8px', fontSize: '12px' }}
                                  onClick={() => handleViewDocumentation(server)}
                                >
                                  Docs
                                </button>
                              )}
                              <button
                                className="btn btn-danger"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                onClick={() => handleDelete(server.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Documentation Viewer Modal */}
      {showDocumentation && selectedServer && (
        <Portal>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem',
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
              borderRadius: '8px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              margin: 'auto',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDocumentation(false);
                  setSelectedServer(null);
                }}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  zIndex: 10000,
                  background: 'rgba(255,255,255,0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                ×
              </button>
              <DocumentationViewer
                documentation={selectedServer.documentation}
                title={selectedServer.name}
                iconUrl={selectedServer.iconUrl}
                iconAlt={selectedServer.iconAlt}
                category={selectedServer.category}
                tags={selectedServer.tags}
                lastDocumentedAt={selectedServer.lastDocumentedAt}
                documentedBy={selectedServer.documentedBy}
                onEdit={() => {
                  setShowDocumentation(false);
                  handleEditDocumentation(selectedServer);
                }}
                showEditButton={true}
              />
            </div>
          </div>
        </Portal>
      )}

      {/* Documentation Editor Modal */}
      {showDocumentationEditor && selectedServer && (
        <Portal>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem',
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
              borderRadius: '8px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              margin: 'auto',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDocumentationEditor(false);
                  setSelectedServer(null);
                }}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  zIndex: 10000,
                  background: 'rgba(255,255,255,0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                ×
              </button>
              <DocumentationEditor
                initialDocumentation={selectedServer.documentation || ''}
                initialCategory={selectedServer.category || ''}
                initialTags={selectedServer.tags || ''}
                initialIconAlt={selectedServer.iconAlt || ''}
                onSave={handleSaveDocumentation}
                onCancel={() => {
                  setShowDocumentationEditor(false);
                  setSelectedServer(null);
                }}
                title={`Edit Documentation - ${selectedServer.name}`}
              />
            </div>
          </div>
        </Portal>
      )}

      {/* Icon Upload Modal */}
      {showIconUpload && selectedServer && (
        <Portal>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem',
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
              borderRadius: '8px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              margin: 'auto',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowIconUpload(false);
                  setSelectedServer(null);
                }}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  zIndex: 10000,
                  background: 'rgba(255,255,255,0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                ×
              </button>
              <IconUpload
                currentIconUrl={selectedServer.iconUrl}
                currentIconAlt={selectedServer.iconAlt}
                onUpload={handleUploadIcon}
                onRemove={handleRemoveIcon}
                onAltTextChange={handleIconAltChange}
                isUploading={isUploadingIcon}
              />
            </div>
          </div>
        </Portal>
      )}

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, serverId: null, isBulk: false })}
        onConfirm={confirmDelete}
        title={deleteConfirm.isBulk ? `Delete ${selectedServers.length} MCP Server(s)?` : 'Delete MCP Server?'}
        message={deleteConfirm.isBulk 
          ? `Are you sure you want to delete ${selectedServers.length} MCP server(s)? This action cannot be undone.`
          : 'Are you sure you want to delete this MCP server? This action cannot be undone.'}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonStyle="danger"
      />
    </div>
  );
};

export default MCPServerManagement;
